import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import type { TalksPageData, TalkData } from "content-ui/talks/types";

import { fetchNavbarAndFooter } from "lib/fetchers/common";
import {
  fetchTalksSlugData,
  fetchTalksPageData,
} from "content-ui/talks/fetcher";

import TalkEventView from "content-ui/talks/talksEventView";
import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import { useEffect } from "react";
import { useRouter } from "next/router";

export async function getServerSideProps({ res, params, preview = false }) {
  const [menuItems, dato, allTalks] = await Promise.all([
    fetchNavbarAndFooter(),
    fetchTalksSlugData({ slug: params.slug[0], preview }),
    fetchTalksPageData({ preview }),
  ]);

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=10, stale-while-revalidate=59"
  );

  if (!dato[0]) return { notFound: true };

  return {
    props: {
      dato,
      allTalks: allTalks.talksData,
      slug: params.slug[0],
      canonical: buildCanonicalUrl(`/talks/${params.slug[0]}`),
      previewMode: preview,
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}

type PageProps = {
  dato: TalksPageData;
  allTalks: TalkData[];
  previewMode: boolean;
  slug: string;
  canonical: string;
};

const TalksPage = (props: PageProps) => {
  const talk = props.dato[0];
  const router = useRouter();
  useEffect(() => {
    const talkRedirect = localStorage.getItem("talk_redirect");

    if (talkRedirect) {
      const allTalks = props.allTalks;
      const targetTalk = allTalks.find((t) => t.slug === talkRedirect);

      if (targetTalk) {
        localStorage.removeItem("talk_redirect");

        router.push(`/talks/${targetTalk.slug}`);
      } else {
        localStorage.removeItem("talk_redirect");
      }
    }
  }, [props.allTalks, router]);

  const title = talk?.seo?.title || talk?.title;
  const description = talk?.seo?.description || talk?.aboutTheEvent;
  const seoImage = talk?.seo?.image?.url || talk?.image?.url;

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: props.canonical,
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <Navigation />

      {props.previewMode ? (
        <PreviewModeBanner exitSlug={`/talks/${props.slug}`} />
      ) : null}

      <ErrorBoundary>
        <TalkEventView talk={talk} allTalks={props.allTalks} />
      </ErrorBoundary>

      <Footer />
    </>
  );
};

export default TalksPage;
